// フォント一覧。同梱フォント + システムフォント名の候補。Local Font Access API (Chromium) で拡張できる。
import { ref } from 'vue'

/**
 * アプリに同梱している書体 (public/fonts、すべて SIL OFL 1.1)。
 * これがあると、どの PC でも同じ結果で日本語を組める。
 */
export const BUNDLED_FONTS = ['Noto Sans JP', 'Noto Serif JP', 'BIZ UDPGothic'] as const
const bundled = new Set<string>(BUNDLED_FONTS)

export const DEFAULT_FONTS = [
  ...BUNDLED_FONTS,
  'sans-serif',
  'serif',
  'monospace',
  'Hiragino Sans',
  'Hiragino Mincho ProN',
  'Yu Gothic',
  'Yu Mincho',
  'Meiryo',
  'Arial',
  'Helvetica Neue',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Impact'
]

export const fontList = ref<string[]>([...DEFAULT_FONTS])

/** 直前の保存で、フォント自身が同梱を許可していなかったもの (ダイアログが読む) */
export const fontEmbedWarnings = ref<import('./fontEmbedding').FontEmbedIssue[]>([])
export const localFontsLoaded = ref(false)

/** 読み込んだフォントファイルの実体。プロジェクトに埋め込んで持ち回れるように原本を保持する */
export interface UserFont {
  family: string
  filename: string
  data: Uint8Array
}

const userFonts = new Map<string, UserFont>()
export function isUserFont(family: string): boolean {
  return userFonts.has(family)
}

/** 文書のテキストレイヤーが使っている、読み込み済みフォントファイル (プロジェクトへ埋め込む対象) */
export function usedUserFonts(doc: { layers: Record<string, { kind: string; style?: { fontFamily?: string } }> }): UserFont[] {
  const out: UserFont[] = []
  const seen = new Set<string>()
  for (const l of Object.values(doc.layers)) {
    if (l.kind !== 'text') continue
    const family = l.style?.fontFamily
    if (!family || seen.has(family)) continue
    seen.add(family)
    const f = userFonts.get(family)
    if (f) out.push(f)
  }
  return out
}

export class FontLoadError extends Error {}

/**
 * フォントファイル (.ttf / .otf / .woff / .woff2) を読み込んで使えるようにする。
 * ファミリ名は指定が無ければファイル名 (拡張子を除く) にする — 推測されるより予測できる方がよい。
 * 保存はしないので、再読み込みすると消える (そのときは fontAvailable: false で分かる)。
 */
export async function addFontFile(data: ArrayBuffer, filename: string, family?: string): Promise<string> {
  if (typeof FontFace === 'undefined' || typeof document === 'undefined') throw new FontLoadError('この環境ではフォントを読み込めない')
  const name = (family ?? filename.replace(/\.[^.]+$/, '')).trim()
  if (!name) throw new FontLoadError('ファミリ名が空')
  let face: FontFace
  try {
    face = await new FontFace(name, data).load()
  } catch (e) {
    throw new FontLoadError(`フォントとして読めない: ${filename} (${e instanceof Error ? e.message : String(e)})`)
  }
  document.fonts.add(face)
  // 原本を控える (FontFace は中身を返してくれない)。プロジェクト保存時に埋め込む
  userFonts.set(name, { family: name, filename, data: new Uint8Array(data.slice(0)) })
  availability.delete(name)
  if (!fontList.value.includes(name)) fontList.value = [name, ...fontList.value]
  for (const fn of readyListeners) fn()
  return name
}

export function canQueryLocalFonts(): boolean {
  return typeof (window as unknown as { queryLocalFonts?: unknown }).queryLocalFonts === 'function'
}

/** ローカルフォントの一覧を取得して fontList に加える (ユーザー操作から呼ぶこと) */
export async function loadLocalFonts(): Promise<number> {
  const q = (window as unknown as { queryLocalFonts?: () => Promise<{ family: string }[]> }).queryLocalFonts
  if (!q) return 0
  const fonts = await q()
  const families = new Set(fontList.value)
  for (const f of fonts) families.add(f.family)
  fontList.value = [...families]
  localFontsLoaded.value = true
  return fonts.length
}

const GENERIC = /^(sans-serif|serif|monospace|cursive|fantasy|system-ui|ui-sans-serif|ui-serif|ui-monospace)$/i

function quoteFamily(family: string): string {
  return /[\s"']/.test(family) && !GENERIC.test(family) ? `"${family.replace(/"/g, '')}"` : family
}

export function cssFont(style: { fontFamily: string; fontSize: number; fontWeight: number; italic: boolean }): string {
  return `${style.italic ? 'italic ' : ''}${style.fontWeight} ${style.fontSize}px ${quoteFamily(style.fontFamily)}`
}

// --- 実際に使えるフォントかどうか ---
//
// document.fonts.check はシステム導入フォントに対して常に true を返すので当てにならない。
// 「指定の書体 + 総称ファミリ」と「総称ファミリ単独」で同じ文字列の幅を比べ、
// 違えば指定が効いている = そのフォントが存在する、と判定する (古典的な手法)。

const PROBE = 'mmmwwwiiilllWM漢字あア0123'
const availability = new Map<string, boolean>()
let probeCtx: CanvasRenderingContext2D | null | undefined

function ctxFor(): CanvasRenderingContext2D | null {
  if (probeCtx !== undefined) return probeCtx
  probeCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d')
  return probeCtx
}

function widthWith(ctx: CanvasRenderingContext2D, font: string): number {
  ctx.font = `48px ${font}`
  return ctx.measureText(PROBE).width
}

/** その書体がこの環境に存在するか。存在しなければ描画は別の書体に落ちる */
export function isFontAvailable(family: string): boolean {
  if (GENERIC.test(family) || bundled.has(family) || userFonts.has(family)) return true
  const cached = availability.get(family)
  if (cached !== undefined) return cached
  const ctx = ctxFor()
  if (!ctx) return true // 判定できない環境では否定しない
  const quoted = quoteFamily(family)
  let found = false
  for (const fallback of ['monospace', 'serif', 'sans-serif']) {
    if (widthWith(ctx, `${quoted}, ${fallback}`) !== widthWith(ctx, fallback)) {
      found = true
      break
    }
  }
  availability.set(family, found)
  return found
}

/** 一覧と、それぞれが実際に使えるか */
export function listFonts(): { family: string; available: boolean }[] {
  return fontList.value.map(family => ({ family, available: isFontAvailable(family) }))
}

// --- 同梱フォントの読み込み ---
//
// unicode-range で分割してあるので、ブラウザは「その文字を含むサブセット」だけを落とす。
// ただし落ち終わるまでは代替の書体で描かれるため、読み込み完了を待って描き直す必要がある。
// ラスタライズ自体は同期処理なので、ここでは「まだなら読み込みを始めて false を返す」だけにして、
// 完了したら購読者 (レンダラ) にキャッシュを捨てさせる。

const readyListeners = new Set<() => void>()
const loading = new Set<string>()

/** 同梱フォントの読み込みが終わったときに呼ばれる。戻り値で解除 */
export function onFontsReady(fn: () => void): () => void {
  readyListeners.add(fn)
  return () => readyListeners.delete(fn)
}

/** 描画前に呼ぶ。その文字を今すぐ正しい書体で描けるなら true */
export function ensureFont(style: { fontFamily: string; fontWeight: number; italic: boolean }, text: string): boolean {
  if (!bundled.has(style.fontFamily) || typeof document === 'undefined' || !document.fonts) return true
  const spec = `${style.italic ? 'italic ' : ''}${style.fontWeight} 16px ${quoteFamily(style.fontFamily)}`
  // 同梱フォントは FontFaceSet に載るので check が当てになる (システムフォントと違う)
  try {
    if (document.fonts.check(spec, text)) return true
  } catch {
    return true // 書体名が CSS として不正なときなど
  }
  if (!loading.has(spec)) {
    loading.add(spec)
    document.fonts
      .load(spec, text)
      .catch(() => undefined)
      .finally(() => {
        loading.delete(spec)
        for (const fn of readyListeners) fn()
      })
  }
  return false
}
